/**
 * File: web/src/lib/errors.ts
 * ==========================
 *
 * Frontend error types (auth + authorization + friendly network messaging)
 *
 * Purpose:
 * - Provide typed errors that UI code can catch and respond to appropriately:
 *   - AuthError: user must sign in again
 *   - ForbiddenError: user is signed in but not allowed
 *
 * When used:
 * - api.ts throws these based on HTTP status codes.
 * - Routes/components can branch on these error types.
 */
export class AuthError extends Error {
  constructor(message = "Authentication required") {
    super(message);
    this.name = "AuthError";
  }
}

export class ForbiddenError extends Error {
  constructor(message = "Not authorized") {
    super(message);
    this.name = "ForbiddenError";
  }
}

/**
 * Convert low-level network errors into a more actionable message.
 *
 * Why:
 * - CORS failures often look like generic fetch errors with no status code.
 * - This message nudges developers to check:
 *   - API Gateway CORS allowOrigins
 *   - S3 upload bucket CORS (for presigned PUTs)
 *   - correct config.json values
 */
export function friendlyNetworkError(original?: unknown) {
  const msg =
    original instanceof Error ? original.message : String(original || "");

  // Common browser phrasing for network/CORS problems.
  if (/failed to fetch/i.test(msg) || /networkerror/i.test(msg)) {
    return "Network/CORS error: unable to reach the API. Please refresh and try again. If the problem persists, sign out and sign in again.";
  }

  return msg || "Unexpected error";
}
